import { useState } from "react";
import { Link } from "react-router-dom";
import { bankInfoCardId, businessInfoCardId, ctaBusinessSettingId, personalInfoCardId } from "@/assets/googleTrackIds";
import { DashboardRoutes } from "@/router";
import { useAppDispatch, useAppSelector, useAppTranslation } from "@/utils";
import { DashboardChecksTypeEnum, WizardStatusEnum } from "@payping/api";
import { CheckCircleOutlined, CloseCircleOutlined, PhRClockCountdown } from "@payping/icons";
import { Button, Card, Collapse, Flex, Grid, Skeleton, Steps, Typography } from "@payping/web-components";
import { buildUrl } from "@payping/utilities-shared/url";
import { getUserChecks } from "@payping/redux/actions";
import { useAsync } from "react-use";
import { URLS } from "@payping/utilities-shared/consts";
import { UserSignContract } from "./sign-contract";

enum StepsEnum {
  identityInformation = 0,
  businessInformation = 1,
  bankInformation = 2,
}

interface Props {
  withdrawStatusLoading: boolean;
}
const CompleteProfile = ({ withdrawStatusLoading }: Props) => {
  const { t } = useAppTranslation();
  const { xl } = Grid.useBreakpoint();
  const dispatch = useAppDispatch();

  const checks = useAppSelector((state) => state.user.checks);
  const haveTerminal = useAppSelector((state) => state.user?.personalInfo?.haveTerminal);
  const hasWithdraw = useAppSelector((state) => state.user.overview.hasWithdraw);
  const tasksStatus = useAppSelector((state) => state.user.overview.tasksStatus);
  const wizard = useAppSelector((state) => state.user.wizard);

  const hasValidContract = useAppSelector((state) => state.user.hasValidContract);

  const { loading: loadingChecks } = useAsync(async () => {
    await dispatch(getUserChecks());
  }, [dispatch]);

  const loading = withdrawStatusLoading || loadingChecks;

  const hideSection =
    tasksStatus?.hasCoupon &&
    tasksStatus.hasCustomer &&
    tasksStatus.hasInvoices &&
    tasksStatus.hasProduct &&
    tasksStatus.hasToken &&
    tasksStatus.hasTransaction;

  const personalSectionStatus = Math.min(
    Number(checks?.personalStatus || 0),
    Number(checks?.birthCertificateStatus || 0),
  );
  const businessSectionStatus = Math.min(
    checks?.isSiteRequired ? Number(checks.siteStatus || 0) : DashboardChecksTypeEnum.Confirmed,
    Number(checks?.businessStatus || 0),
    Number(checks?.businessRegistrationStatus || 0),
    Number(checks?.localStatus || 0),
  );
  const bankSectionStatus = Number(checks?.bankStatus || 0);
  const isProfileCompleted =
    personalSectionStatus === DashboardChecksTypeEnum.Confirmed &&
    businessSectionStatus === DashboardChecksTypeEnum.Confirmed &&
    bankSectionStatus === DashboardChecksTypeEnum.Confirmed;

  const [currentStep, setCurrentStep] = useState(() => {
    switch (DashboardChecksTypeEnum.NotFilled) {
      case personalSectionStatus:
        return StepsEnum.identityInformation;
      case businessSectionStatus:
        return StepsEnum.businessInformation;
      case bankSectionStatus:
        return StepsEnum.bankInformation;
      default:
        switch (DashboardChecksTypeEnum.AdminPending) {
          case personalSectionStatus:
            return StepsEnum.identityInformation;
          case businessSectionStatus:
            return StepsEnum.businessInformation;
          case bankSectionStatus:
            return StepsEnum.bankInformation;
          default:
            return StepsEnum.identityInformation;
        }
    }
  });

  const itemSubTitle = (status: number) => {
    switch (status) {
      case DashboardChecksTypeEnum.NotFilled:
        return t("overview.completeProfile.status.notFilled");
      case DashboardChecksTypeEnum.AdminPending:
        return t("overview.completeProfile.status.pending");
      case DashboardChecksTypeEnum.Confirmed:
        return t("overview.completeProfile.status.confirmed");
      default:
        return undefined;
    }
  };
  const itemIcon = (status: number) => {
    switch (status) {
      case DashboardChecksTypeEnum.NotFilled:
        return <CloseCircleOutlined width={"16px"} className="color--danger" />;
      case DashboardChecksTypeEnum.AdminPending:
        return <PhRClockCountdown width={"16px"} className="color--warning" />;
      case DashboardChecksTypeEnum.Confirmed:
        return <CheckCircleOutlined width={"16px"} className="color--success" />;
      default:
        return undefined;
    }
  };
  const identityInformationContent = (
    <Flex vertical className={"xl:tw-mt-4"}>
      <Typography.Title level={3}>{t(`overview.completeProfile.identityInformation.title`)}</Typography.Title>
      <Typography.Paragraph className="color--gray500">
        {t(`overview.completeProfile.identityInformation.content`)}
      </Typography.Paragraph>
      {isProfileCompleted ? null : (
        <div>
          {personalSectionStatus === DashboardChecksTypeEnum.Confirmed ? (
            <Button disabled type="primary">
              {t(`overview.completeProfile.identityInformation.actions.edit`)}
            </Button>
          ) : (
            <Link
              to={
                personalSectionStatus === DashboardChecksTypeEnum.AdminPending
                  ? DashboardRoutes.settings.personal.edit.url
                  : DashboardRoutes.register.personal.url
              }
              data-google-track={personalInfoCardId}
            >
              <Button type="primary">
                {t(
                  `overview.completeProfile.identityInformation.actions.${
                    personalSectionStatus === DashboardChecksTypeEnum.AdminPending ? "edit" : "submit"
                  }`,
                )}
              </Button>
            </Link>
          )}
        </div>
      )}
    </Flex>
  );
  const businessInformationContent = (
    <Flex vertical className={"xl:tw-mt-4"}>
      <Typography.Title level={3}>{t(`overview.completeProfile.businessInformation.title`)}</Typography.Title>
      <Typography.Paragraph className="color--gray500">
        {t(`overview.completeProfile.businessInformation.content`)}
      </Typography.Paragraph>
      {isProfileCompleted ? null : (
        <div>
          {businessSectionStatus === DashboardChecksTypeEnum.Confirmed ? (
            <Button disabled type="primary">
              {t(`overview.completeProfile.businessInformation.actions.edit`)}
            </Button>
          ) : (
            <Link
              to={
                businessSectionStatus === DashboardChecksTypeEnum.AdminPending
                  ? DashboardRoutes.settings.business.edit.url
                  : DashboardRoutes.register.business.url
              }
              data-google-track={businessInfoCardId}
            >
              <Button type="primary">
                {t(
                  `overview.completeProfile.businessInformation.actions.${
                    businessSectionStatus === DashboardChecksTypeEnum.AdminPending ? "edit" : "submit"
                  }`,
                )}
              </Button>
            </Link>
          )}
        </div>
      )}
    </Flex>
  );
  const bankInformationContent = (
    <Flex vertical className={"xl:tw-mt-4"}>
      <Typography.Title level={3}>{t(`overview.completeProfile.bankInformation.title`)}</Typography.Title>
      <Typography.Paragraph className="color--gray500">
        {t(`overview.completeProfile.bankInformation.content`)}
      </Typography.Paragraph>
      {isProfileCompleted ? null : (
        <div>
          {bankSectionStatus === DashboardChecksTypeEnum.Confirmed ? (
            <Button disabled type="primary">
              {t(`overview.completeProfile.bankInformation.actions.edit`)}
            </Button>
          ) : (
            <Link
              to={
                bankSectionStatus === DashboardChecksTypeEnum.AdminPending
                  ? DashboardRoutes.settings.bank.edit.url
                  : DashboardRoutes.register.bank.url
              }
              data-google-track={bankInfoCardId}
            >
              <Button type="primary">
                {t(
                  `overview.completeProfile.bankInformation.actions.${
                    bankSectionStatus === DashboardChecksTypeEnum.AdminPending ? "edit" : "submit"
                  }`,
                )}
              </Button>
            </Link>
          )}
        </div>
      )}
    </Flex>
  );

  const successfulTerminalCard = (
    <Card.Ant className={"tw-w-full"}>
      <Flex vertical gap="12px">
        <Typography.Text className="color--gray500 tw-mb-4">
          {t("overview.completeProfile.informationConfirmed.upperTitle")}
        </Typography.Text>
        <CheckCircleOutlined className="color--success" width={"36px"} />
        <Typography.Title className="!tw-m-0" level={4}>
          {t("overview.completeProfile.informationConfirmed.title")}
        </Typography.Title>
        <Typography.Text className="color--gray500">
          {t("overview.completeProfile.informationConfirmed.content")}
        </Typography.Text>
        <Link to={DashboardRoutes.settings.business.root.url} data-google-track={ctaBusinessSettingId}>
          <Button>{t("overview.completeProfile.informationConfirmed.actions.BusinessSettings")}</Button>
        </Link>
      </Flex>
    </Card.Ant>
  );
  const pendingTerminalCard = (
    <Card.Ant className={"tw-w-full"}>
      <Flex align={"center"} gap={"small"}>
        <img
          alt={"Pending Shaparak"}
          width={80}
          height={80}
          src={buildUrl(URLS.STORAGE_URL, "img/home/pending_icon.png").toString()}
          // preview={false}
        />
        <Flex justify={"space-between"} vertical>
          <Typography.Title level={4}>{t("overview.completeProfile.pendingShaparak.title")}</Typography.Title>
          <Typography.Text type={"secondary"}>
            {t("overview.completeProfile.pendingShaparak.description")}
          </Typography.Text>
        </Flex>
      </Flex>
    </Card.Ant>
  );
  const desktopCard = (
    <Card.Ant className={"tw-w-full"}>
      <Flex vertical className="tw-w-full">
        <Steps
          type="navigation"
          size="small"
          current={currentStep}
          onChange={(cur) => setCurrentStep(cur)}
          className="site-navigation-steps"
          items={[
            {
              title: t("overview.completeProfile.identityInformation.title"),
              subTitle: itemSubTitle(personalSectionStatus),
              icon: itemIcon(personalSectionStatus),
            },
            {
              title: t("overview.completeProfile.businessInformation.title"),
              subTitle: itemSubTitle(businessSectionStatus),
              icon: itemIcon(businessSectionStatus),
            },
            {
              title: t("overview.completeProfile.bankInformation.title"),
              subTitle: itemSubTitle(bankSectionStatus),
              icon: itemIcon(bankSectionStatus),
            },
          ]}
        />
        {currentStep === StepsEnum.identityInformation ? identityInformationContent : null}
        {currentStep === StepsEnum.businessInformation ? businessInformationContent : null}
        {currentStep === StepsEnum.bankInformation ? bankInformationContent : null}
      </Flex>
    </Card.Ant>
  );
  const mobileCard = (
    <Card.Ant
      className={"tw-w-full"}
      styles={{
        body: {
          padding: 0,
        },
      }}
    >
      <Collapse
        bordered={false}
        ghost
        expandIconPosition="end"
        activeKey={currentStep}
        onChange={(key) => {
          if (key[1]) {
            setCurrentStep(Number(key[1]));
          }
        }}
        items={[
          {
            key: StepsEnum.identityInformation,
            label: (
              <Flex align="center" gap="4px">
                {itemIcon(personalSectionStatus)}
                <Typography.Title className="!tw-m-0" level={5}>
                  {t("overview.completeProfile.identityInformation.title")}
                </Typography.Title>
                <Typography.Text className="color--gray500">{itemSubTitle(personalSectionStatus)}</Typography.Text>
              </Flex>
            ),
            children: identityInformationContent,
            className: "border-bottom border--gray100",
          },
          {
            key: StepsEnum.businessInformation,
            label: (
              <Flex align="center" gap="4px">
                {itemIcon(businessSectionStatus)}
                <Typography.Title className="!tw-m-0" level={5}>
                  {t("overview.completeProfile.businessInformation.title")}
                </Typography.Title>
                <Typography.Text className="color--gray500">{itemSubTitle(businessSectionStatus)}</Typography.Text>
              </Flex>
            ),
            children: businessInformationContent,
            className: "border-bottom border--gray100",
          },
          {
            key: StepsEnum.bankInformation,
            label: (
              <Flex align="center" gap="4px">
                {itemIcon(bankSectionStatus)}
                <Typography.Title className="!tw-m-0" level={5}>
                  {t("overview.completeProfile.bankInformation.title")}
                </Typography.Title>
                <Typography.Text className="color--gray500">{itemSubTitle(bankSectionStatus)}</Typography.Text>
              </Flex>
            ),
            children: bankInformationContent,
          },
        ]}
      />
    </Card.Ant>
  );

  const shaparakRejectedCard = (
    <Card.Ant className={"tw-w-full"}>
      <Flex align={"center"} gap={"small"}>
        <img
          alt={"Pending Shaparak"}
          width={80}
          height={80}
          src={buildUrl(URLS.STORAGE_URL, "img/home/shaparakRejected.png").toString()}
          // preview={false}
        />
        <Flex justify={"space-between"} vertical>
          <Typography.Title level={4}>{t("overview.completeProfile.shaparakRejected.title")}</Typography.Title>
          <Typography.Text type={"secondary"}>
            {t("overview.completeProfile.shaparakRejected.description", { rejectedReason: wizard?.reason })}
          </Typography.Text>
        </Flex>
      </Flex>
    </Card.Ant>
  );
  if (loading) return <Skeleton active paragraph />;

  if (haveTerminal && !hasValidContract) {
    return <UserSignContract />;
  }

  if (hideSection) return null;

  if (hasWithdraw) return null;

  if (wizard?.userInfoStatus === WizardStatusEnum.ShaparakPending) return pendingTerminalCard;

  if (wizard?.userInfoStatus === WizardStatusEnum.ShaparakRejected) return shaparakRejectedCard;

  if (haveTerminal) return successfulTerminalCard;

  return xl ? desktopCard : mobileCard;
};

export default CompleteProfile;
